import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { Input } from '@workspace/ui/components/input'
import { useState } from 'react'
import { useUpdateInvestorMutation } from '../../../hooks/api'

interface DealFlowPreferencesSectionProps {
  investorId: string
  minPreScreenTotalScore: number | null
  minAiSessionTotalScore: number | null
}

// Empty input → no threshold; otherwise clamp to a 0–100 integer.
function parseScore(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return null
  return Math.max(0, Math.min(100, Math.round(n)))
}

export function DealFlowPreferencesSection({
  investorId,
  minPreScreenTotalScore,
  minAiSessionTotalScore,
}: DealFlowPreferencesSectionProps) {
  const [preScreen, setPreScreen] = useState(minPreScreenTotalScore?.toString() ?? '')
  const [aiSession, setAiSession] = useState(minAiSessionTotalScore?.toString() ?? '')
  const updateMutation = useUpdateInvestorMutation(investorId)

  const parsedPreScreen = parseScore(preScreen)
  const parsedAiSession = parseScore(aiSession)
  const isDirty =
    parsedPreScreen !== minPreScreenTotalScore || parsedAiSession !== minAiSessionTotalScore

  const handleSave = () => {
    updateMutation.mutate({
      path: { investorId },
      body: {
        minPreScreenTotalScore: parsedPreScreen,
        minAiSessionTotalScore: parsedAiSession,
      },
    })
  }

  return (
    <section className="mt-10 rounded-xl border border-border/60 bg-card/30 p-6">
      <h2 className="text-base font-semibold text-foreground">Deal Flow</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Hide matches whose total score is below your threshold. Total score is the AI Session score
        when available, otherwise the Pre-screen council score. Leave empty to see everything.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor="min-pre-screen-score"
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            Min Pre-screen score
          </label>
          <Input
            id="min-pre-screen-score"
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            placeholder="0–100"
            value={preScreen}
            onChange={(e) => setPreScreen(e.target.value)}
            className="mt-1.5"
          />
        </div>
        <div>
          <label
            htmlFor="min-ai-session-score"
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            Min AI Session score
          </label>
          <Input
            id="min-ai-session-score"
            type="number"
            inputMode="numeric"
            min={0}
            max={100}
            placeholder="0–100"
            value={aiSession}
            onChange={(e) => setAiSession(e.target.value)}
            className="mt-1.5"
          />
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <CtaButton
          size="sm"
          onClick={handleSave}
          disabled={!isDirty}
          loading={updateMutation.isPending}
        >
          Save
        </CtaButton>
      </div>
    </section>
  )
}
